import type { AdminShopCategory, SaveShopProductRequest, ShopProduct, ShopVariant } from "../../../api/shop";
import { isSkuCode, PRODUCT_CODE_MAX, SKU_MAX } from "../shared/codes";
import type { CatalogShape, Combinations, StylePreview } from "./combinations";
import { TOO_MANY_COMBINATIONS, expandCombinations, previewSku, skuKey, styleKey } from "./combinations";

export type ProductKind = "simple" | "variants";

/** One SKU's settings in the editor. Text fields hold what the admin typed. */
export type SkuDraft = {
  /** The saved SKU row: the server keeps it (stock, orders) while its combination stays chosen */
  id?: string;
  /** "" = generated on save */
  sku: string;
  stock: string;
  /** The stock when the editor opened, sent as expectedStock (the server refuses the save if orders moved it) */
  openedStock: number | null;
  /** "" = the base price */
  priceOverride: string;
  isEnabled: boolean;
};

export type ProductForm = {
  name: string;
  description: string;
  /** The top-level category */
  categoryId: string;
  /** "" when the category has none, or none is picked yet */
  subcategoryId: string;
  sportIds: string[];
  price: string;
  /** "" = generated on save */
  code: string;
  /** false: a new product's code follows suggest-code and is sent blank (the server generates the same one) */
  codeTouched: boolean;
  trackStock: boolean;
  isActive: boolean;
  kind: ProductKind;
  /** Kept while the type is Simple, so switching back restores them */
  optionValueIds: string[];
  sizeIds: string[];
  /** styleKey → photos, first = cover */
  styleImages: Record<string, string[]>;
  /** skuKey → settings; a combination not listed gets the defaults */
  skus: Record<string, SkuDraft>;
  /**
   * The saved product is one style with no values (simple, size-only or migrated). Once values are picked, the
   * server hands that style to the FIRST new style: see editorView.
   */
  carriesBlankStyle: boolean;
};

export type ErrorTarget =
  | { kind: "field"; field: "name" | "price" | "category" | "subcategory" | "code" }
  | { kind: "section"; section: "variants" | "photos" | "combinations" }
  | { kind: "style"; key: string }
  | { kind: "sku"; key: string };

export type FormProblem = { message: string; target: ErrorTarget | null };
export type RemovedSkus = { count: number; stock: number; labels: string[] };
/** Photos of a style that is gone, offered to the new style that contains its values ("Use for Black · Cotton") */
export type PhotoOffer = { fromLabel: string; toKey: string; toLabel: string; urls: string[] };

const DEFAULT_DRAFT: SkuDraft = { sku: "", stock: "0", openedStock: null, priceOverride: "", isEnabled: true };

export const emptyForm = (): ProductForm => ({
  name: "",
  description: "",
  categoryId: "",
  subcategoryId: "",
  sportIds: [],
  price: "",
  code: "",
  codeTouched: false,
  trackStock: true,
  isActive: true,
  kind: "simple",
  optionValueIds: [],
  sizeIds: [],
  styleImages: {},
  skus: {},
  carriesBlankStyle: false,
});

/** The saved SKUs by combination key (their style's values + their size). */
export function savedSkusByKey(product: ShopProduct): Map<string, ShopVariant> {
  const valuesByStyle = new Map(product.styles.map((s) => [s.id, s.optionValueIds] as const));
  return new Map(product.variants.map((v) => [skuKey(styleKey(valuesByStyle.get(v.styleId) ?? []), v.sizeId), v] as const));
}

const draftFromVariant = (v: ShopVariant): SkuDraft => ({
  id: v.id,
  sku: v.sku || "",
  stock: String(v.stock ?? 0),
  openedStock: v.stock,
  priceOverride: v.priceOverride != null ? String(v.priceOverride) : "",
  isEnabled: v.isEnabled,
});

/** Saved as one style with no values: the style the server carries over to the first new style. */
const isBlankStyled = (product: Pick<ShopProduct, "styles">) => product.styles.length === 1 && product.styles[0].optionValueIds.length === 0;

export function formFromProduct(product: ShopProduct): ProductForm {
  const styleImages: Record<string, string[]> = {};
  product.styles.forEach((s) => {
    styleImages[styleKey(s.optionValueIds)] = [...s.imageUrls];
  });
  const skus: Record<string, SkuDraft> = {};
  savedSkusByKey(product).forEach((v, key) => {
    skus[key] = draftFromVariant(v);
  });
  const optionValueIds = product.optionTypes.flatMap((t) => t.values.map((v) => v.id));
  const sizeIds = product.sizes.map((s) => s.id);
  return {
    name: product.name,
    description: product.description || "",
    categoryId: product.categoryId || "",
    subcategoryId: product.subcategoryId || "",
    sportIds: [...product.sportIds],
    price: String(product.price),
    code: product.code || "",
    codeTouched: true,
    trackStock: product.trackStock,
    isActive: product.isActive,
    kind: optionValueIds.length === 0 && sizeIds.length === 0 ? "simple" : "variants",
    optionValueIds,
    sizeIds,
    styleImages,
    skus,
    carriesBlankStyle: isBlankStyled(product),
  };
}

/** What the product is made of right now: Simple ignores the kept variant picks. */
export const effectivePicks = (form: Pick<ProductForm, "kind" | "optionValueIds" | "sizeIds">): { optionValueIds: string[]; sizeIds: string[] } =>
  form.kind === "simple" ? { optionValueIds: [], sizeIds: [] } : { optionValueIds: form.optionValueIds, sizeIds: form.sizeIds };

export const formCombinations = (form: Pick<ProductForm, "kind" | "optionValueIds" | "sizeIds">, catalog: CatalogShape): Combinations => {
  const picks = effectivePicks(form);
  return expandCombinations(catalog, picks.optionValueIds, picks.sizeIds);
};

/** A SKU's settings, or the defaults for a combination nobody touched (on, stock 0, generated SKU, base price). */
export const draftFor = (form: Pick<ProductForm, "skus">, key: string): SkuDraft => form.skus[key] ?? DEFAULT_DRAFT;

type CarryFields = Pick<ProductForm, "skus" | "styleImages" | "carriesBlankStyle">;

const firstStyleOnceValuesPicked = (carries: boolean, combos: Combinations): StylePreview | null =>
  carries && !combos.tooMany && combos.optionValueIds.length > 0 ? combos.styles[0] ?? null : null;

/**
 * The server's carry-over: a product saved as one style with no values that now picks values hands that style to
 * the FIRST new style (option-type order, then value order). Its SKUs (no values, size) become (first style, size),
 * keeping their id, stock, expected stock, SKU code, price and switch; its photos go to the first style when the
 * save sends none for it. The first style's key, or null when nothing carries over.
 */
export const carriedStyleKey = (form: Pick<ProductForm, "carriesBlankStyle">, combos: Combinations): string | null =>
  firstStyleOnceValuesPicked(form.carriesBlankStyle, combos)?.key ?? null;

/** Where a row's settings live: a carried row is still the old SKU ("|M"), so it follows whichever style is first. */
const rawSkuKey = (form: Pick<ProductForm, "skus">, carry: string | null, key: string): string => {
  if (carry === null || !key.startsWith(`${carry}|`)) return key;
  const blank = key.slice(carry.length);
  return form.skus[blank] ? blank : key;
};

/** Where a style's photos live: the first style shows (and edits) the old style's photos while it has none. */
const rawImageKey = (form: Pick<ProductForm, "styleImages">, carry: string | null, key: string): string =>
  carry !== null && key === carry && (form.styleImages[key] ?? []).length === 0 ? "" : key;

/** The form as the editor shows and saves it: with the carried SKUs and photos on the first style. */
export function editorView<F extends CarryFields>(form: F, combos: Combinations): F {
  const carry = carriedStyleKey(form, combos);
  if (carry === null) return form;
  const skus = { ...form.skus };
  combos.skus.forEach((row) => {
    const raw = rawSkuKey(form, carry, row.key);
    if (raw !== row.key) skus[row.key] = form.skus[raw];
  });
  const styleImages =
    rawImageKey(form, carry, carry) === carry ? form.styleImages : { ...form.styleImages, [carry]: form.styleImages[""] ?? [] };
  return { ...form, skus, styleImages };
}

/** The category node the product is filed under: the subcategory when one is picked. */
export const leafCategoryId = (form: Pick<ProductForm, "categoryId" | "subcategoryId">) => form.subcategoryId || form.categoryId;

/** The `data-target` of the element an error belongs to. */
export const targetId = (target: ErrorTarget): string =>
  target.kind === "field" ? `field:${target.field}` : target.kind === "section" ? `section:${target.section}` : `${target.kind}:${target.key}`;

/** Where to scroll for an error: a field scrolls to the Basics section. */
export const scrollTargetId = (target: ErrorTarget): string => (target.kind === "field" ? "section:basics" : targetId(target));

/** Styles with a switched-on SKU and no photo: flagged in Photos; activating needs a photo on each. */
export function missingPhotoKeys(input: CarryFields, combos: Combinations): Set<string> {
  const form = editorView(input, combos);
  const missing = new Set<string>();
  combos.styles.forEach((style) => {
    const on = combos.skus.some((row) => row.styleKey === style.key && draftFor(form, row.key).isEnabled);
    if (on && (form.styleImages[style.key] ?? []).length === 0) missing.add(style.key);
  });
  return missing;
}

/**
 * Photos no style below shows, so the save drops them: an old style's photos once its value set changed (Black
 * after Cotton is added). Each is offered to the first new style that contains the old style's values
 * (Black → Black · Cotton). The old no-values style is in every new style; it usually carries over (editorView).
 */
export function unlinkedPhotos(input: CarryFields, combos: Combinations): PhotoOffer[] {
  if (combos.tooMany) return [];
  const form = editorView(input, combos);
  const current = new Set(combos.styles.map((s) => s.key));
  const used = new Set(combos.styles.flatMap((s) => form.styleImages[s.key] ?? []));
  const offers: { at: number; offer: PhotoOffer }[] = [];
  Object.entries(form.styleImages).forEach(([key, urls]) => {
    if (current.has(key)) return;
    const free = urls.filter((url) => !used.has(url));
    if (free.length === 0) return;
    const ids = key ? key.split("+") : [];
    const at = combos.styles.findIndex((s) => ids.every((id) => s.optionValueIds.includes(id)));
    if (at < 0) return;
    const to = combos.styles[at];
    const fromLabel = to.values.filter((v) => ids.includes(v.id)).map((v) => v.label).join(" · ");
    offers.push({ at, offer: { fromLabel, toKey: to.key, toLabel: to.label, urls: free } });
  });
  return offers.sort((a, b) => a.at - b.at).map((o) => o.offer);
}

/** Change a style's photos (functional, so uploads that finish together don't overwrite each other). */
export function updateStylePhotos(form: ProductForm, combos: Combinations, key: string, update: (urls: string[]) => string[]): ProductForm {
  const raw = rawImageKey(form, carriedStyleKey(form, combos), key);
  return { ...form, styleImages: { ...form.styleImages, [raw]: update(form.styleImages[raw] ?? []) } };
}

const fieldTarget = (field: Extract<ErrorTarget, { kind: "field" }>["field"]): ErrorTarget => ({ kind: "field", field });

const parseWhole = (text: string): number | null => {
  const t = text.trim();
  if (t === "") return 0;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

const parseOverride = (text: string): number | null | "invalid" => {
  const t = text.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : "invalid";
};

/** The editor as its save request, or the first problem (with where it belongs). */
export function buildSaveRequest(
  input: ProductForm,
  catalog: { categories: AdminShopCategory[] },
  combos: Combinations
): SaveShopProductRequest | FormProblem {
  const form = editorView(input, combos);
  const name = form.name.trim();
  if (!name) return { message: "Enter a name", target: fieldTarget("name") };
  const price = Number(form.price);
  if (form.price.trim() === "" || !Number.isFinite(price) || price <= 0) {
    return { message: "Price must be more than 0", target: fieldTarget("price") };
  }
  const category = catalog.categories.find((c) => c.id === form.categoryId);
  if (!category) return { message: "Choose a category", target: fieldTarget("category") };
  if (category.subcategories.length > 0 && !category.subcategories.some((s) => s.id === form.subcategoryId)) {
    return { message: `Choose a subcategory of ${category.name}`, target: fieldTarget("subcategory") };
  }
  const code = form.code.trim();
  if (form.codeTouched && code && !isSkuCode(code, PRODUCT_CODE_MAX)) {
    return { message: "Product code: up to 32 letters, digits or -", target: fieldTarget("code") };
  }
  if (combos.tooMany) return { message: TOO_MANY_COMBINATIONS, target: { kind: "section", section: "variants" } };

  const skus: SaveShopProductRequest["skus"] = [];
  const seen = new Map<string, string>();
  for (const row of combos.skus) {
    const draft = draftFor(form, row.key);
    const target: ErrorTarget = { kind: "sku", key: row.key };
    const typed = draft.sku.trim().toUpperCase();
    if (typed && !isSkuCode(typed, SKU_MAX)) return { message: `SKU for ${row.label}: up to 64 letters, digits or -`, target };
    const effective = typed || previewSku(code, row.values, row.size);
    const twin = effective ? seen.get(effective) : undefined;
    if (twin) return { message: `SKU ${effective} is used twice (${twin} and ${row.label})`, target };
    if (effective) seen.set(effective, row.label);
    const stock = form.trackStock ? parseWhole(draft.stock) : null;
    if (form.trackStock && stock === null) return { message: `Stock for ${row.label} must be a whole number of 0 or more`, target };
    const override = parseOverride(draft.priceOverride);
    if (override === "invalid") return { message: `Price for ${row.label} must be more than 0`, target };
    skus.push({
      optionValueIds: row.optionValueIds,
      sizeId: row.sizeId,
      sku: typed || null,
      stock,
      expectedStock: draft.id ? draft.openedStock : null,
      priceOverride: override,
      isEnabled: draft.isEnabled,
    });
  }

  // Drafts (inactive) save without these checks
  if (form.isActive) {
    if (!skus.some((s) => s.isEnabled)) {
      return { message: "Switch on at least one SKU, or untick Active to save a draft", target: { kind: "section", section: "combinations" } };
    }
    const missing = missingPhotoKeys(form, combos);
    const style = combos.styles.find((s) => missing.has(s.key));
    if (style) return { message: style.label ? `Add a photo for ${style.label}` : "Add a photo", target: { kind: "style", key: style.key } };
  }

  return {
    name,
    description: form.description.trim() || null,
    categoryId: leafCategoryId(form),
    sportIds: form.sportIds,
    price,
    code: form.codeTouched ? code || null : null,
    trackStock: form.trackStock,
    isActive: form.isActive,
    optionValueIds: combos.optionValueIds,
    sizeIds: combos.sizeIds,
    styles: combos.styles.map((s) => ({ optionValueIds: s.optionValueIds, imageUrls: form.styleImages[s.key] ?? [] })),
    skus,
  };
}

export const isFormProblem = (result: SaveShopProductRequest | FormProblem): result is FormProblem => "message" in result;

/**
 * The saved SKUs this save would delete (their combination is no longer chosen), with the stock they hold. SKUs the
 * server carries over to the first new style (see editorView) stay.
 */
export function removedSkus(product: ShopProduct | null, combos: Combinations): RemovedSkus {
  if (!product || combos.tooMany) return { count: 0, stock: 0, labels: [] };
  const kept = new Set(combos.skus.map((row) => row.key));
  const carry = firstStyleOnceValuesPicked(isBlankStyled(product), combos)?.key ?? null;
  const gone = Array.from(savedSkusByKey(product))
    .filter(([key]) => !kept.has(carry !== null && key.startsWith("|") ? `${carry}${key}` : key))
    .map(([, variant]) => variant);
  return {
    count: gone.length,
    stock: gone.reduce((n, v) => n + Math.max(0, v.stock ?? 0), 0),
    labels: gone.map((v) => v.label),
  };
}

/** Change one row. Pass the combinations so a carried row (see editorView) edits the old SKU it is. */
export function updateSku(form: ProductForm, key: string, patch: Partial<SkuDraft>, combos?: Combinations): ProductForm {
  const raw = rawSkuKey(form, combos ? carriedStyleKey(form, combos) : null, key);
  return { ...form, skus: { ...form.skus, [raw]: { ...draftFor(form, raw), ...patch } } };
}

/** Apply `change` to every row's settings (as the editor shows them), writing each where it lives. */
function updateRows(form: ProductForm, combos: Combinations, change: (draft: SkuDraft) => SkuDraft | null): ProductForm {
  const carry = carriedStyleKey(form, combos);
  const view = editorView(form, combos);
  const skus = { ...form.skus };
  combos.skus.forEach((row) => {
    const next = change(draftFor(view, row.key));
    if (next) skus[rawSkuKey(form, carry, row.key)] = next;
  });
  return { ...form, skus };
}

/** "Set stock for all": every switched-on SKU gets this stock (saved with the product). */
export const setStockForAll = (form: ProductForm, combos: Combinations, stock: number): ProductForm =>
  updateRows(form, combos, (draft) => (draft.isEnabled ? { ...draft, stock: String(stock) } : null));

/** "Rebuild SKUs": blank every SKU so the next save generates them all from the current codes. */
export const rebuildSkus = (form: ProductForm, combos: Combinations): ProductForm => updateRows(form, combos, (draft) => ({ ...draft, sku: "" }));

/**
 * After a "Stock for … changed" refusal, with the product read again: every saved SKU expects its fresh stock, and
 * takes it as its stock unless the admin changed that stock. Every other edit stays. Matched by SKU id, so a carried
 * row (see editorView) is refreshed too. A SKU deleted meanwhile becomes a new row; one created meanwhile is added.
 */
export function refreshStock(form: ProductForm, fresh: ShopProduct): ProductForm {
  const byId = new Map(fresh.variants.map((v) => [v.id, v] as const));
  const claimed = new Set<string>();
  const untouched = (draft: SkuDraft) => parseWhole(draft.stock) === (draft.openedStock ?? 0);
  const adopt = (draft: SkuDraft, v: ShopVariant): SkuDraft => {
    claimed.add(v.id);
    return { ...draft, id: v.id, openedStock: v.stock, stock: untouched(draft) ? String(v.stock ?? 0) : draft.stock };
  };
  const skus: Record<string, SkuDraft> = {};
  Object.entries(form.skus).forEach(([key, draft]) => {
    const v = draft.id ? byId.get(draft.id) : undefined;
    if (v) skus[key] = adopt(draft, v);
    else if (draft.id) {
      const gone: SkuDraft = { ...draft, openedStock: null };
      delete gone.id;
      skus[key] = gone;
    } else skus[key] = draft;
  });
  savedSkusByKey(fresh).forEach((v, key) => {
    if (claimed.has(v.id)) return;
    const draft = skus[key];
    skus[key] = draft && !draft.id ? adopt(draft, v) : draft ?? draftFromVariant(v);
  });
  return { ...form, skus };
}

/** What the admin can see and save, to tell whether closing loses anything (a suggested code doesn't count). */
function snapshot(form: ProductForm, catalog: CatalogShape): string {
  const combos = formCombinations(form, catalog);
  const view = editorView(form, combos);
  const sorted = (ids: string[]) => [...ids].sort();
  return JSON.stringify([
    form.name,
    form.description,
    form.categoryId,
    form.subcategoryId,
    sorted(form.sportIds),
    form.price,
    form.codeTouched ? form.code : null,
    form.trackStock,
    form.isActive,
    form.kind,
    sorted(form.optionValueIds),
    sorted(form.sizeIds),
    combos.skus.map((row) => {
      const d = draftFor(view, row.key);
      return [row.key, d.id ?? null, d.sku, d.stock, d.openedStock, d.priceOverride, d.isEnabled];
    }),
    combos.styles.map((s) => [s.key, view.styleImages[s.key] ?? []]),
  ]);
}

/** The admin changed something since `start` (changes undone don't count). */
export const isFormDirty = (start: ProductForm, form: ProductForm, catalog: CatalogShape): boolean =>
  snapshot(start, catalog) !== snapshot(form, catalog);
